import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { fetchSharedSummary } from "../api/client.js";

export function SharedView() {
   const { token } = useParams();
   const [data, setData] = useState(null);
   const [error, setError] = useState(false);
   const [loading, setLoading] = useState(true);

   useEffect(() => {
      fetchSharedSummary(token)
         .then((d) => {
            if (d) setData(d);
            else setError(true);
         })
         .catch(() => setError(true))
         .finally(() => setLoading(false));
   }, [token]);

   if (loading)
      return (
         <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <p className="text-slate-400">Loading…</p>
         </div>
      );

   if (error)
      return (
         <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="text-center">
               <h1 className="text-lg font-semibold text-slate-900 mb-2">
                  Link not found
               </h1>
               <p className="text-sm text-slate-500">
                  This share link is invalid or has been revoked.
               </p>
            </div>
         </div>
      );

   const {
      displayName,
      statusCounts,
      totalApplications,
      overdueCount,
      needsNextStepCount,
      lastActivity,
      storyCount,
      weeklyData,
      weeklyActivity,
      networkingSummary,
      goals,
      staleCount,
   } = data;

   return (
      <div className="min-h-screen bg-slate-50 p-6">
         <div className="max-w-md mx-auto space-y-6">
            <div className="text-center">
               <h1 className="text-lg font-bold text-primary-700">Launchpad</h1>
               <p className="text-sm text-slate-700 mt-1">
                  {displayName
                     ? `${displayName}'s Job Search`
                     : "Job Search Progress"}
               </p>
               <p className="text-xs text-slate-400 mt-0.5">
                  Last active{" "}
                  {lastActivity
                     ? new Date(lastActivity).toLocaleDateString()
                     : "—"}
               </p>
            </div>

            {/* At a Glance — networking-first */}
            <div className="rounded border border-slate-200 bg-white p-4">
               <h2 className="text-sm font-medium text-slate-700 mb-3">
                  At a Glance
               </h2>
               <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                     <p className="text-2xl font-bold text-slate-900">
                        {networkingSummary
                           ? networkingSummary.outreach +
                             networkingSummary.scheduled +
                             (networkingSummary.nurturing || 0)
                           : 0}
                     </p>
                     <p className="text-[10px] text-slate-500">
                        Active Outreach
                     </p>
                     {networkingSummary && networkingSummary.thisWeek > 0 && (
                        <p className="text-[9px] text-primary-500">↑ {networkingSummary.thisWeek} this week</p>
                     )}
                  </div>
                  <div>
                     <p className="text-2xl font-bold text-slate-900">
                        {networkingSummary
                           ? networkingSummary.scheduled +
                             (networkingSummary.nurturing || 0)
                           : 0}
                     </p>
                     <p className="text-[10px] text-slate-500">Conversations</p>
                  </div>
                  <div>
                     <p className="text-2xl font-bold text-slate-900">
                        {(statusCounts.saved || 0) +
                           statusCounts.applied +
                           statusCounts.interviewing +
                           statusCounts.offer}
                     </p>
                     <p className="text-[10px] text-slate-500">Active Apps</p>
                  </div>
               </div>
            </div>

            {/* Needs Attention */}
            {(overdueCount > 0 || needsNextStepCount > 0 || staleCount > 0) && (
               <div className="rounded border border-slate-200 bg-white p-4 space-y-2">
                  <h2 className="text-sm font-medium text-slate-700">
                     Needs Attention
                  </h2>
                  {overdueCount > 0 && (
                     <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">
                           Overdue follow-ups
                        </span>
                        <span className="text-sm font-medium text-red-600">
                           {overdueCount}
                        </span>
                     </div>
                  )}
                  {staleCount > 0 && (
                     <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">
                           Stale relationships (14+ days)
                        </span>
                        <span className="text-sm font-medium text-purple-600">
                           {staleCount}
                        </span>
                     </div>
                  )}
                  {needsNextStepCount > 0 && (
                     <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">
                           Needs next step
                        </span>
                        <span className="text-sm font-medium text-amber-600">
                           {needsNextStepCount}
                        </span>
                     </div>
                  )}
               </div>
            )}

            {/* This Week's Moves */}
            {weeklyActivity && weeklyActivity.length > 0 && (
               <div className="rounded border border-slate-200 bg-white p-4">
                  <h2 className="text-sm font-medium text-slate-700 mb-3">
                     This Week's Moves
                  </h2>
                  <ul className="space-y-1">
                     {weeklyActivity.map((entry, i) => (
                        <li key={i} className="text-sm text-slate-600">
                           <span className="capitalize">{entry.from}</span>
                           {" → "}
                           <span className="capitalize">{entry.to}</span>
                           <span className="text-xs text-slate-400 ml-2">
                              {new Date(entry.timestamp).toLocaleDateString()}
                           </span>
                        </li>
                     ))}
                  </ul>
               </div>
            )}

            {/* Goals */}
            {goals && goals.length > 0 && (
               <div className="rounded border border-slate-200 bg-white p-4">
                  <h2 className="text-sm font-medium text-slate-700 mb-3">
                     🎯 Weekly Goals
                  </h2>
                  <div className="space-y-2">
                     {goals.map((goal, i) => (
                        <div key={i} className="space-y-1">
                           <div className="flex justify-between text-xs">
                              <span className="text-slate-600">
                                 {goal.title}
                              </span>
                              <span
                                 className={`font-medium ${goal.current >= goal.target ? "text-green-600" : "text-slate-500"}`}
                              >
                                 {goal.current >= goal.target ? "✓ " : ""}
                                 {goal.current}/{goal.target}
                              </span>
                           </div>
                           <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                              <div
                                 className={`h-full rounded ${goal.current >= goal.target ? "bg-green-500" : "bg-primary-500"}`}
                                 style={{
                                    width: `${Math.min(100, Math.round((goal.current / goal.target) * 100))}%`,
                                 }}
                              />
                           </div>
                        </div>
                     ))}
                  </div>
               </div>
            )}

            {/* Networking Pipeline */}
            {networkingSummary &&
               (networkingSummary.targets > 0 ||
                  networkingSummary.outreach > 0 ||
                  networkingSummary.scheduled > 0) && (
                  <div className="rounded border border-slate-200 bg-white p-4">
                     <h2 className="text-sm font-medium text-slate-700 mb-3">
                        🤝 Networking Pipeline
                     </h2>
                     <div className="grid grid-cols-4 gap-2 text-center">
                        {[
                           {
                              label: "Targets",
                              count: networkingSummary.targets,
                           },
                           {
                              label: "Outreach",
                              count: networkingSummary.outreach,
                           },
                           {
                              label: "Chats",
                              count: networkingSummary.scheduled,
                           },
                           {
                              label: "Nurturing",
                              count: networkingSummary.nurturing || 0,
                           },
                        ].map(({ label, count }) => (
                           <div key={label}>
                              <p className="text-lg font-bold text-slate-900">
                                 {count}
                              </p>
                              <p className="text-[10px] text-slate-500">
                                 {label}
                              </p>
                           </div>
                        ))}
                     </div>
                  </div>
               )}

            {/* Application Funnel */}
            {totalApplications > 0 && (
               <div className="rounded border border-slate-200 bg-white p-4">
                  <h2 className="text-sm font-medium text-slate-700 mb-3">
                     Applications ({totalApplications} total)
                  </h2>
                  <div className="space-y-1.5">
                     {Object.entries(statusCounts)
                        .filter(([, count]) => count > 0)
                        .map(([status, count]) => {
                           const pct = (count / totalApplications) * 100;
                           return (
                              <div
                                 key={status}
                                 className="flex items-center gap-2"
                              >
                                 <span className="w-20 text-xs text-slate-600 capitalize">
                                    {status}
                                 </span>
                                 <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden">
                                    <div
                                       className="h-full bg-primary-400 rounded"
                                       style={{ width: `${pct}%` }}
                                    />
                                 </div>
                                 <span className="w-6 text-xs text-slate-500 text-right">
                                    {count}
                                 </span>
                              </div>
                           );
                        })}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">{totalApplications > 0 ? Math.round(((statusCounts.interviewing + statusCounts.offer) / totalApplications) * 100) : 0}% interview rate</p>
               </div>
            )}

            {/* Weekly Activity Chart */}
            {weeklyData && (
               <div className="rounded border border-slate-200 bg-white p-4">
                  <h2 className="text-sm font-medium text-slate-700 mb-3">
                     Weekly Application Activity
                  </h2>
                  <div className="flex items-end gap-2 h-12">
                     {weeklyData.map((count, i) => (
                        <div
                           key={i}
                           className="flex-1 flex flex-col items-center gap-1"
                        >
                           <span className="text-xs text-slate-500">
                              {count}
                           </span>
                           <div
                              className="w-full bg-primary-400 rounded-t min-h-0.5"
                              style={{
                                 height: `${Math.max(4, (count / Math.max(...weeklyData, 1)) * 100)}%`,
                              }}
                           />
                        </div>
                     ))}
                  </div>
                  <div className="flex justify-between mt-1 text-[10px] text-slate-400">
                     <span>4w ago</span>
                     <span>3w ago</span>
                     <span>2w ago</span>
                     <span>This week</span>
                  </div>
               </div>
            )}

            <p className="text-center text-xs text-slate-400">
               Powered by Launchpad
            </p>
         </div>
      </div>
   );
}
